import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

/**
 * Keeps a report page restorable across a round trip to a detail page.
 *
 * The report pages hold their filters in local refs, so clicking a customer and then pressing
 * "← กลับ" (CsPortfolio's button is router.go(-1)) used to remount them with every filter back at
 * its default and the table scrolled to the top — the whole view the user had set up was gone.
 * This puts the filters in the query string, which the history entry carries, and the scroll
 * offsets in sessionStorage.
 *
 * Call it AFTER the filter refs exist but BEFORE any watcher that loads data: it seeds the refs
 * synchronously, so a loader registered later with { immediate: true } already sees the restored
 * values and fetches once rather than twice.
 *
 * @param routeName  name of this page's route — every query write targets it by name
 * @param filters    { <queryKey>: { ref, default, type?, values? } }
 *                   type   'string' | 'number' | 'boolean', inferred from `default` unless null
 *                   values allowed values (array) or a predicate; anything else falls back to
 *                          `default`, so a hand-edited URL can't put the page in a state its
 *                          config maps don't cover
 * @param ready      () => boolean — true once the rows are rendered AND visible. A v-show'd table
 *                   is display:none while loading and silently refuses to take a scrollTop, so the
 *                   loading flag belongs in here, not just the row count.
 * @returns { scrollBox } — bind to the scrolling element with ref="scrollBox"
 */
export function useRestorableView({ routeName, filters, ready }) {
  const route = useRoute()
  const router = useRouter()
  const specs = Object.entries(filters)

  // ── filters ⇄ query string ───────────────────────────────────────────────
  function decode(key, spec) {
    const raw = route.query[key]
    const str = Array.isArray(raw) ? raw[0] : raw
    if (str == null) return spec.default
    const type = spec.type || (spec.default === null ? 'string' : typeof spec.default)
    let v = str
    if (type === 'boolean') v = str === '1'
    else if (type === 'number') {
      v = Number(str)
      if (!Number.isFinite(v)) return spec.default
    }
    const allowed = Array.isArray(spec.values) ? spec.values.includes(v)
      : typeof spec.values === 'function' ? spec.values(v)
        : true
    return allowed ? v : spec.default
  }

  function seed() {
    for (const [key, spec] of specs) spec.ref.value = decode(key, spec)
  }

  // Only non-default values are written, so an untouched page keeps a clean URL.
  function queryFromFilters() {
    const query = {}
    for (const [key, spec] of specs) {
      const v = spec.ref.value
      if (v === spec.default) continue
      query[key] = typeof v === 'boolean' ? '1' : String(v)
    }
    return query
  }

  seed()

  // ── scroll offsets ───────────────────────────────────────────────────────
  const scrollBox = ref(null)
  const storeKey = `nvtx:viewScroll:${routeName}`
  let pending = null
  try {
    const raw = sessionStorage.getItem(storeKey)
    sessionStorage.removeItem(storeKey)   // consumed once, so a later fresh visit starts at the top
    const saved = raw ? JSON.parse(raw) : null
    // Only the exact view it was taken from: a different filter set is a different list of rows,
    // and dropping the user halfway down it would be worse than starting at the top.
    if (saved && saved.path === route.fullPath) pending = saved
  } catch (_e) { /* private mode, quota, or junk under the key — restoring scroll is a nicety */ }

  // Two offsets, because two things scroll. The table has its own max-height and scrolls inside it;
  // the page scrolls behind it — but NOT on the window: the theme's
  // <main class="main-content … overflow-x-hidden"> leaves overflow-y at visible, which CSS then
  // computes to auto because the other axis is hidden, so <main> is the scroller and
  // window.scrollTo is a no-op. Walk up to whatever actually scrolls instead of naming the theme's
  // class here.
  function pageScroller() {
    let node = scrollBox.value ? scrollBox.value.parentElement : null
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY
      if (overflowY === 'auto' || overflowY === 'scroll') return node
      node = node.parentElement
    }
    return document.scrollingElement || document.documentElement
  }

  // An element that is display:none — CsTop100 keeps its table in the DOM behind v-show while the
  // API call is in flight — or that has not reached its full height yet reports
  // scrollHeight === clientHeight and swallows scrollTop without complaint. The offset is spent on
  // the first write, so wait for proof that the write can actually land. (CsForecast never hit this
  // because its table is built by v-if and only exists once the rows do.)
  function canTake(el, offset) {
    return !offset || (!!el && el.scrollHeight > el.clientHeight)
  }

  function applyScroll() {
    if (!pending || (ready && !ready())) return
    const box = scrollBox.value
    const scroller = pageScroller()
    if (!canTake(box, pending.box) || !canTake(scroller, pending.page)) return
    const { page = 0, box: boxTop = 0 } = pending
    pending = null
    if (box) box.scrollTop = boxTop
    if (scroller) scroller.scrollTop = page
  }

  // The list can take more than one frame to reach full height: the request resolves, the rows
  // render, the enrichment from the score store can render them again. Rather than bet on one
  // frame being the right one, retry cheaply until the offset lands or the window closes — it only
  // ever applies once, so nothing here can fight the reader.
  let deadline = 0
  let abortOnInput = null

  function armAbort() {
    if (abortOnInput) return
    // Once the reader scrolls for themselves, the restore has lost the right to move them.
    abortOnInput = () => { pending = null; disarmAbort() }
    window.addEventListener('wheel', abortOnInput, { passive: true })
    window.addEventListener('touchstart', abortOnInput, { passive: true })
    window.addEventListener('keydown', abortOnInput)
  }

  function disarmAbort() {
    if (!abortOnInput) return
    window.removeEventListener('wheel', abortOnInput)
    window.removeEventListener('touchstart', abortOnInput)
    window.removeEventListener('keydown', abortOnInput)
    abortOnInput = null
  }

  function tick() {
    applyScroll()
    if (!pending || Date.now() >= deadline) disarmAbort()
    else requestAnimationFrame(tick)
  }

  function restoreScroll() {
    if (!pending) return
    const alreadyRunning = Date.now() < deadline
    deadline = Date.now() + 700
    if (!alreadyRunning) { armAbort(); tick() }
  }

  // `ready` closes over consts declared further down the caller's setup, so it must not run until
  // setup has finished — hence registering its watcher here rather than at call time.
  onMounted(() => {
    restoreScroll()
    if (ready) watch(ready, restoreScroll, { flush: 'post' })
  })

  onBeforeUnmount(() => { pending = null; disarmAbort() })

  onBeforeRouteLeave((to, from) => {
    try {
      const scroller = pageScroller()
      sessionStorage.setItem(storeKey, JSON.stringify({
        path: from.fullPath,
        page: scroller ? scroller.scrollTop : 0,
        box: scrollBox.value ? scrollBox.value.scrollTop : 0,
      }))
    } catch (_e) { /* a failed save must never block the navigation */ }
  })

  // ── keep the two in step ─────────────────────────────────────────────────
  // `replace`, not `push`: a burst of filter clicks must not pile up history entries, or "← กลับ"
  // from the detail page would walk back through them one click at a time.
  watch(specs.map(([, spec]) => spec.ref), () => {
    if (route.name !== routeName) return
    pending = null            // the filters moved under us; that saved offset is for another list
    const query = queryFromFilters()
    const keys = Object.keys(query)
    const unchanged = keys.length === Object.keys(route.query).length
      && keys.every(k => route.query[k] === query[k])
    if (!unchanged) router.replace({ name: routeName, query })
  })

  // …and the other direction. Landing on this route while it is already open reuses the component
  // instead of remounting it (the sidebar's own link to this page, browser back/forward), so
  // without this the table would keep filters the URL no longer names.
  watch(() => route.query, () => {
    if (route.name === routeName) seed()
  })

  return { scrollBox }
}
